import { useMemo } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import type { ItemImage, ItemVariant, PriceInfo } from 'root/apiTypes';
import { useGetDisplayStylesParams } from 'root/hooks/useGetDisplayStylesParams';
import type { ItemProps } from './Item';
import { isSectionChecked, isPriceShowed } from 'root/utils/display';
import { PricingType } from 'root/clientTypes';
import type { MenuCheckbox } from 'root/clientTypes';
import { useGetDisplaySettingsParams } from 'root/hooks/useGetDisplaySettingsParams';
import { useItemsContext } from 'root/contexts/ItemsContext';
import { useGetHtmlTag } from 'root/hooks/useGetHtmlTag';
import { useSettings } from '@wix/tpa-settings/react';
import { DesignSettingsParams } from 'root/components/Menus/Settings/Tabs/DesignTab/DesignSettingsParams';
import { useFeaturedBadgeLogic } from 'root/components/Menus/FeaturedBadge/FeaturedBadge.logic';
import { hasPriceVariants as getHasPriceVariants } from 'root/utils/itemPricing';

const getFormattedPrice = (priceInfo: PriceInfo | undefined, showCurrency: boolean) =>
  showCurrency ? priceInfo?.formattedPrice : priceInfo?.formattedPriceNoCurrency ?? '';

const getHiddenVariantsPriceContent = ({
  variants,
  showCurrency,
  showZeroPrice,
  sectionChecked,
}: {
  variants: ItemVariant[];
  showCurrency: boolean;
  showZeroPrice: boolean;
  sectionChecked: boolean;
}) => {
  const visibleVariantPrices = variants
    .filter((variant) =>
      isPriceShowed({
        price: variant.priceInfo?.price ?? '',
        showZeroPrice,
        sectionChecked,
      })
    )
    .map((variant) => getFormattedPrice(variant.priceInfo, showCurrency))
    .filter(Boolean);

  if (visibleVariantPrices.length === 0) {
    return undefined;
  }

  const firstPrice = visibleVariantPrices[0];
  const lastPrice = visibleVariantPrices[visibleVariantPrices.length - 1];

  return firstPrice === lastPrice ? firstPrice : `${firstPrice} - ${lastPrice}`;
};

export const useItemLogic = ({ item }: ItemProps) => {
  const { sectionId, discountInfoByItemId, itemBehavior } = useItemsContext();
  const discountInfo = item.id ? discountInfoByItemId?.[item.id] : undefined;
  const { showItemCurrency, showItemVariantsCurrency } = useGetDisplayStylesParams();
  const { t } = useTranslation();
  const isMarketPrice = item.pricingType === PricingType.MARKET_PRICE;

  const {
    checkPricingMenus,
    checkPlaceholderMenu,
    imagePlaceholder,
    showZeroPrice,
    showZeroPriceToAll,
    showImagePlaceholder,
    showPlaceholderToAll,
  } = useGetDisplaySettingsParams();

  const itemTitleHtmlTag = useGetHtmlTag('itemTitleTheme');
  const itemDescriptionHtmlTag = useGetHtmlTag('itemDescriptionTheme');
  const itemPriceHtmlTag = useGetHtmlTag('itemPriceTheme');

  const settings = useSettings();

  const itemHasPriceVariants = getHasPriceVariants(item);
  const hasHiddenPriceVariants = !!itemBehavior?.hidePriceVariants && itemHasPriceVariants;
  const hasPriceVariants = itemBehavior?.hidePriceVariants ? false : itemHasPriceVariants;

  const priceOptionSectionChecked =
    showZeroPriceToAll || isSectionChecked(checkPricingMenus as MenuCheckbox[], sectionId ?? '');

  const hiddenVariantsPriceContent = hasHiddenPriceVariants
    ? getHiddenVariantsPriceContent({
        variants: item.priceVariants?.variants ?? [],
        showCurrency: showItemVariantsCurrency,
        showZeroPrice: showZeroPrice as boolean,
        sectionChecked: priceOptionSectionChecked as boolean,
      })
    : undefined;

  const itemPriceContent = isMarketPrice
    ? t('app.item.market-price')
    : showItemCurrency
    ? item.priceInfo?.formattedPrice
    : item.priceInfo?.formattedPriceNoCurrency ?? '';

  const priceContent = hiddenVariantsPriceContent ?? itemPriceContent;

  const shouldShowPrice =
    isMarketPrice ||
    !!hiddenVariantsPriceContent ||
    isPriceShowed({
      price: item.priceInfo?.price ?? '',
      showZeroPrice: showZeroPrice as boolean,
      sectionChecked: priceOptionSectionChecked as boolean,
    });
  const shouldShowLabels = item.labels && item.labels.length > 0;

  const imagePlaceholderSectionChecked =
    showPlaceholderToAll || isSectionChecked(checkPlaceholderMenu as MenuCheckbox[], sectionId ?? '');

  const shouldShowModifierGroups =
    !itemBehavior?.hideModifierGroups && item.modifierGroups && item.modifierGroups?.length > 0;
  const shouldShowImagePlaceholder = useMemo(
    () => (showImagePlaceholder ? imagePlaceholderSectionChecked : false),
    [imagePlaceholderSectionChecked, showImagePlaceholder]
  );

  const itemImage = useMemo(
    () => (item?.image ? item?.image : shouldShowImagePlaceholder ? imagePlaceholder : undefined) as ItemImage,
    [imagePlaceholder, item?.image, shouldShowImagePlaceholder]
  );

  const isFeaturedItem = item.featured;
  const shouldOverrideItemDesign = isFeaturedItem && settings.get(DesignSettingsParams.overrideItemDesign);

  const { isVisible: isFeaturedBadgeVisible, isOnImage: isFeaturedBadgeOnImage } = useFeaturedBadgeLogic({
    featuredType: item.featuredInfo?.type,
  });

  // A badge that sits on the image needs the image container to stay put even when this
  // particular item has no image of its own — otherwise it has nothing to sit on.
  const shouldReserveImageContainer =
    !itemImage && !!isFeaturedItem && isFeaturedBadgeVisible && isFeaturedBadgeOnImage;

  const discountLabel = discountInfo && !discountInfo.hidePriceDisplay ? discountInfo.label : undefined;
  const discountedPrice = discountInfo && !discountInfo.hidePriceDisplay ? discountInfo.formattedFinalPrice : undefined;
  const variantDiscounts = discountInfo && !discountInfo.hidePriceDisplay ? discountInfo.variantDiscounts : undefined;

  return {
    hasPriceVariants,
    isMarketPrice,
    priceContent,
    shouldShowPrice,
    shouldShowLabels,
    shouldShowModifierGroups,
    shouldShowImagePlaceholder,
    itemImage,
    itemTitleHtmlTag,
    itemDescriptionHtmlTag,
    itemPriceHtmlTag,
    isFeaturedItem,
    shouldOverrideItemDesign,
    shouldReserveImageContainer,
    discountLabel,
    discountedPrice,
    variantDiscounts,
    itemAction: itemBehavior?.action,
    disableImageExpand: itemBehavior?.disableImageExpand,
    itemClassName: itemBehavior?.getItemClassName?.(item),
    itemImageResize: itemBehavior?.imageResize,
  };
};
